import { flatten, bind, optimumBy, map, filter, count } from '@vaaas/fpts/iter';
import { of as set, filter as sfilter, outside } from '@vaaas/fpts/set';
import { compose, pipe } from '@vaaas/fpts/function';
import { lt, sign } from '@vaaas/fpts/maths';
import { groupBy, of as as_map } from '@vaaas/fpts/map';
import { suffix, mapr } from '@vaaas/fpts/duad';
import HashTable from "./hashtable";
import { LOG, display_message_to_user } from '@phi/logging/frontend';
import * as THREE from 'three';
import * as maths from './maths';
import * as v3 from './vector3';
import {
  deform_edge,
  end_deform_edge,
  start_deform_edge,
} from './JCore/logging_jcore';
import { WrappedValue } from '@phi/wrapped-value';
import {
    uil_get_faces_of_edge,
    uil_get_body_of_face,
    uil_get_vertices_of_edge,
    uil_is_object_fixed,
    uil_is_vertex_of_face,
    uil_is_vertex_smooth,
    uil_is_object_visible,
    uil_listify_edges,
    uil_get_point_at_edge_param,
    uil_get_tangent_at_edge_param,
    uil_move_vertex_to_coords,
    uil_is_joint_smooth,
} from "./ui_logic";
import App from './application-container';
import { edge_is_for_sketch, face_is_for_sketch, vertex_is_for_sketch } from './reference_sketches';
import { push_nodups, request_setting_button_states, suppress_animate, suppress_undo_tracking, unsuppress_animate_if_ready } from './utility';
import { uid_deselect_all_objects, uid_deselect_object, uid_select_object } from './ui_display';
import { unique } from '@vaaas/fpts/array';

export function collect_all_modified_vertices() {
  var modified_vertices = new Array();
  user_modified_objects_hash.values().forEach(function(o){
    if (o.type == "vertex") {
      if (modified_vertices.indexOf(o) == -1)
        modified_vertices.push(o);
    }
  });
  return (modified_vertices);
}

export function assemble_lists_of_smooth_and_unsmooth_selected_vertices(smooth_vertex_list, unsmooth_vertex_list) {
  if (uid_selected_vertices) {
    uid_selected_vertices.keys().forEach(function(v) {
      // If this model is not closed body and the vertex is on the border of the visible face, don't do anything to it.
      var skip_vertex = false;
      var edge_ids = uil_get_edges_of_vertex(v);

      // If it's a 2-vertex, skip it.
      if (edge_ids.length == 2)
        skip_vertex = true;

      edge_ids.forEach(function(eid){
        var f_array = uil_get_faces_of_edge(eid);
        if (!uil_is_object_visible(f_array[0]) || !uil_is_object_visible(f_array[1]))
        skip_vertex = true;
      });


      if (!skip_vertex) {
        if (uil_is_vertex_smooth(v))
          smooth_vertex_list.push(v);
        else
          unsmooth_vertex_list.push(v);
      }
    });
  }
}

export function assemble_lists_of_smooth_and_unsmooth_selected_joints(smooth_joint_list, unsmooth_joint_list) {
  var edge_pairs = find_all_pairs_in_unlistified_edge_list(uid_selected_edges.keys());
  edge_pairs.forEach(function(ep) {
    if (uil_is_joint_smooth(ep[0], ep[1]) == 1)
      smooth_joint_list.push(ep);
    else if (uil_is_joint_smooth(ep[0], ep[1]) == 0)
      unsmooth_joint_list.push(ep);
  });
}


export function round_selected_edges () {
  var edges = new Array();
  edges = [... uid_selected_edges.keys()];
  uid_deselect_all_objects();
  suppress_undo_tracking();
  edges.forEach( function(eid) {
    if (isEdge(eid) && uil_can_make_edge_smooth(eid))
      uil_make_edge_smooth(eid);
  });
  unsuppress_undo_tracking_if_ready(false);
}

export function un_round_selected_edges () {
  var edges = new Array();
  edges = [... uid_selected_edges.keys()];
  uid_deselect_all_objects();
  edges.forEach( function(eid) {
    if (isEdge(eid) && uil_can_make_edge_nonsmooth(eid))
      uil_make_edge_nonsmooth(eid);
  });
}

export function remove_selected_objects_from_symmetry_set() {
  remove_from_symmetry_set(uid_selected_edges.keys(), uid_selected_faces.keys());
}

export function remove_from_symmetry_set(edge_ids, face_ids) {
  // Create a list with all selected edges or edges of selected faces, without duplicates.
  let all_selected_edges = [];

  edge_ids.forEach(function(eid){
    if (all_selected_edges.indexOf(eid) == -1)
      all_selected_edges.push(eid);
  });

  face_ids.forEach(function(fid){
    var es = uil_get_edges_of_face(fid);
    es.forEach(function(eid) {
      if (all_selected_edges.indexOf(eid) == -1)
        all_selected_edges.push(eid);
    });
  });

  // Remove edges from the symmetry sets. For each edge identified in one set, remove the corresponding one from the
  // other set as well to keep things symmetric.
  for (var i = symmetry_edges_list_first.length - 1; i >= 0; i--) {
    if ( all_selected_edges.indexOf(symmetry_edges_list_first[i]) != -1) {
      symmetry_edges_list_first.splice(i,1);
      symmetry_edges_list_second.splice(i,1);
    }
  }
  for (var i = symmetry_edges_list_second.length - 1; i >= 0; i--) {
    if ( all_selected_edges.indexOf(symmetry_edges_list_second[i]) != -1) {
      symmetry_edges_list_first.splice(i,1);
      symmetry_edges_list_second.splice(i,1);
    }
  }
}

export function add_selected_objects_to_symmetry_set() {
  add_to_symmetry_set(uid_selected_edges.keys(), uid_selected_faces.keys());
}

export function add_to_symmetry_set(edges_to_add, faces_to_add) {
  const edge_positions_vector = eid => new THREE.Vector3()
    .addVectors(
        ...uil_get_vertices_of_edge(eid)
            .map(x =>
                uid_vertices.getItem(x)
                    .object3d
                    .position));

  let bbox = uid_calculate_bounding_box_of_objects([], edges_to_add, faces_to_add, true);

  const position_wrt_sym_planes = x => edge_position_wrt_plane(symmetry_plane_base, symmetry_plane_normal, x, bbox.dimension/50.0);

  const mirror = x => find_mirror_point(x, symmetry_plane_base, symmetry_plane_normal);

  const symmetry_edges = new Set([
    ...symmetry_edges_list_first,
    ...symmetry_edges_list_second,
  ]);

  const all_selected_edges = pipe(
    // First we reject any edges that are already in the symmetry sets
    // Create a list with all selected edges or edges of selected faces, without duplicates.
    [
        edges_to_add,
        bind(uil_get_edges_of_face)(faces_to_add),
    ],
    flatten,
    set,
    // Remove any edges that are already in a symmetry set.
    sfilter(outside(symmetry_edges)),
  );

  if (all_selected_edges.length === 0) {
    display_message_to_user(LOG.WARN, "No new edges to add to symmetry set");
    return;
  }

  // Create two sets of edges, which we will then have to juxtapose based on their geometry.
  // For each edge we will also store its "center" coordinates, based on its endpoints.
  const groups = pipe(
    all_selected_edges,
    map(suffix(edge_positions_vector)),
    groupBy(x => sign(position_wrt_sym_planes(x[0]))),
  );

  const set1 = new Map(groups.get(1) ?? []);

  // For the 2nd set we'll store the reflection of the center w.r.t. the symmetry plane.
  // TODO: use alist
  const set2 = pipe(
    groups.get(-1) ?? [],
    map(mapr(mirror)),
    as_map,
  );

  if (set1.size + set2.size !== all_selected_edges.size)
    display_message_to_user(LOG.INFO, "Some edges were not added to the symmetry set.");

  if (set1.size != set2.size) {
    display_message_to_user(LOG.WARN, "Malformed set of edges for symmetry set");
    return;
  }

  // Now match edges between the two sets, based on the distance between their "centers".
  // Every pair we find we add to the symmetry_edges_list_{first, second}.
  for (const [eid1, v1] of set1) {
    // Find best match for 1st edge in set1
    const [eid2, v2] = optimumBy(([eid2, v2]) => v3.point_point_distance(v1, v2))(lt)(set2);
    symmetry_edges_list_first.push(eid1);
    symmetry_edges_list_second.push(eid2);
    set2.delete(eid2);
  }
}

export function get_list_of_edges_around_faces(face_ids) {
  var edge_hash = new HashTable();
  face_ids.forEach(function(fid){
    var es = uil_get_edges_of_face(fid);
    es.forEach(function(eid) {
      if (edge_hash.hasItem(eid))
        edge_hash.setItem(eid, edge_hash.getItem(eid) + 1);
      else
        edge_hash.setItem(eid, 1);
    });
  });
  var border_edges = [];
  edge_hash.keys().forEach(function(eid){
    if (edge_hash.getItem(eid) == 1)
      border_edges.push(eid);
  });

  return border_edges;
}

export function edge_is_on_face_border(edge_id, face_id) {
  var found = false;
  var edge_array = uil_get_edges_of_face(face_id);
  edge_array.forEach(function(e) {
    if (e == edge_id)
      found = true;
  });
  return (found);
}

export function vertex_is_on_face_border(vertex_id, face_id) {
  var found = false;
  var edge_array = uil_get_edges_of_face(face_id);
  edge_array.forEach(function(e) {
    if (uil_get_vertices_of_edge(e)[0] == vertex_id || uil_get_vertices_of_edge(e)[1] == vertex_id)
      found = true;
  });
  return (found);
}

export function can_delete_edge(eid) {
  // First check if we are in an open surface model and the edge is at the border of the visible surface
  if (uil_is_body_open(uil_get_body_of_edge(eid))) {
    var f_array = uil_get_faces_of_edge(eid);
    if (!uil_is_object_visible(f_array[0]) || !uil_is_object_visible(f_array[1]))
      return 0;
  }
  return 1;
}

export function vertex_is_on_edges(vid, edge_ids) {
  var is_on_edges = false;
  edge_ids.forEach(function(eid){
    var vs = uil_get_vertices_of_edge(eid);
    if (vs[0] == vid || vs[1] == vid)
      is_on_edges = true;
  });
  return is_on_edges;
}

export function vertices_are_on_same_edge(v1id, v2id) {
  var es1 = uil_get_edges_of_vertex(v1id);
  var es2 = uil_get_edges_of_vertex(v2id);
  var on_same_edge = false;
  es1.forEach(function(eid){
    if (es2.indexOf(eid) != -1)
      on_same_edge = true;
  });
  return on_same_edge;
}

export function vertices_of_face(fid) {
  var vs = [];
  var es = uil_get_edges_of_face(fid);
  es.forEach(function(eid){
    var ve = uil_get_vertices_of_edge(eid);
    if (vs.indexOf(ve[0]) == -1)
      vs.push(ve[0]);
    if (vs.indexOf(ve[1]) == -1)
      vs.push(ve[1]);
  });
  return vs
}

export function faces_of_body(bid) {
    return uid_faces.keys().filter(fid => uil_get_body_of_face(fid) === bid);
}

export function find_edge_from_two_vertices(v1id, v2id) {
  var e1s = uil_get_edges_of_vertex(v1id);
  var e2s = uil_get_edges_of_vertex(v2id);
  var eid = null;
  e1s.forEach(function(tempeid) {
    if (e2s.indexOf(tempeid) != -1)
      eid = tempeid;
  });
  return eid;
}

export function faces_of_vertex(vid) {
  return unique(uid_faces.keys().filter(x => vertices_of_face(x).includes(vid)));
}

export function find_face_from_two_vertices(v1id, v2id) {
  var common_fid = 0;
  var es1 = uil_get_edges_of_vertex(v1id) || [];
  var es2 = uil_get_edges_of_vertex(v2id) || [];
  var fs = []
  es1.forEach(function(eid){
    push_nodups(uil_get_faces_of_edge(eid)[0], fs);
    push_nodups(uil_get_faces_of_edge(eid)[1], fs);
  });
  es2.forEach(function(eid){
    push_nodups(uil_get_faces_of_edge(eid)[0], fs);
    push_nodups(uil_get_faces_of_edge(eid)[1], fs);
  });
  fs.forEach(function(fid){
    var vs = vertices_of_face(fid);
    if ((vs.indexOf(v1id) != -1) && (vs.indexOf(v2id) != -1))
      common_fid = fid;
  });
  return common_fid;
}


/*
function internal_vertices_of_faces(face_ids) {
  var internal_vertices = [];
  var border_vs = [];
  circle_edge_ids = uil_get_edges_surround_connected_faces(face_ids);
  circle_edge_ids.forEach(function(eid){
    var evs = uil_get_vertices_of_edge[eid];
    if (border_vs.indexOf(evs[0]) == -1)
      border_vs.push(evs[0]);
    if (border_vs.indexOf(evs[1]) == -1)
      border_vs.push(evs[1]);
  });
  face_ids.forEach(function(fid){
    var vs = vertices_of_face(fid);
    vs.forEach(function(vid){
      if (border_vs.indexOf(vid) == -1)
        internal_vertices.push(vid);
    });
  });
  return internal_vertices;
}
*/

export function faces_form_connected_component(face_ids) {
    var boundary_edge_ids = new Array();
    var boundary_vertex_ids = new Array();
    var wv_is_one_cc = new WrappedValue();
    var res = uil_get_face_connected_component_info(face_ids, wv_is_one_cc, boundary_edge_ids, boundary_vertex_ids);
    return res && wv_is_one_cc.v;
}

export function sort_face_vertices(fid) {
  var face_vertices = new Array();
  uid_vertices.keys().forEach(function(vid){
    if (uil_is_vertex_of_face(vid, fid))
      face_vertices.push(vid);
  });
  var sorted_vertices = new Array();
  var current_vertex = face_vertices[0];
  var last_edge = uil_get_edges_of_vertex(current_vertex)[0];
  while (face_vertices.length > 0) {
    sorted_vertices.push(current_vertex);
    face_vertices.splice(face_vertices.indexOf(current_vertex),1); // Remove vertex from array
    var v_edges = uil_get_edges_of_vertex(current_vertex);
    for (var i = v_edges.length - 1; i >= 0; i--)
      if ( !uil_is_edge_of_face(v_edges[i], fid))
        v_edges.splice(i, 1);

    if (last_edge == v_edges[0])
      last_edge = v_edges[1];
    else
      last_edge = v_edges[0];

    var new_vertex1 = uil_get_vertices_of_edge(last_edge)[0];
    var new_vertex2 = uil_get_vertices_of_edge(last_edge)[1];
    if (sorted_vertices.indexOf(new_vertex1) == -1)
      current_vertex = new_vertex1;
    else
      current_vertex = new_vertex2;
  }
  return sorted_vertices;
}

export function sort_edge_list_vertices(edge_list) {
  var vertex_list = [];
  var wv_is_open_list = new WrappedValue();
  var sorted_edges = [];
  var res = uil_listify_edges(edge_list, wv_is_open_list, sorted_edges);
  if (res) {
    // Also calculate an ordered array of vertex ids
    for (var i = 0; i <= sorted_edges.length-1; i++) {
      var v;
      if (i > 0) {
        v = uil_find_edge_shared_vertex(sorted_edges[i-1], sorted_edges[i]);
      }
      else if (i == 0) {
        var v01 = uil_find_edge_shared_vertex(sorted_edges[0], sorted_edges[1]);
        var v1a = uil_get_vertices_of_edge(sorted_edges[0])[0];
        var v1b = uil_get_vertices_of_edge(sorted_edges[0])[1];
        v = v1a == v01 ? v1b : v1a;
      }
      vertex_list.push(v);
      if (wv_is_open_list.v && i == sorted_edges.length-1 ) {
        var vn = uil_find_edge_shared_vertex(sorted_edges[sorted_edges.length-1], sorted_edges[sorted_edges.length-2]);
        var vna = uil_get_vertices_of_edge(sorted_edges[sorted_edges.length-1])[0];
        var vnb = uil_get_vertices_of_edge(sorted_edges[sorted_edges.length-1])[1];
        v = vna == vn ? vnb : vna;
        vertex_list.push(v);
      }
    }
  }
  return vertex_list;
}

export const find_end_vertices_of_edge_list = compose(
  bind(uil_get_vertices_of_edge),
  count,
  filter(([k, v]) => v === 1),
  map(x => x[0]),
  Array.from,
);

export function get_listified_lists_of_connected_edges_in_edge_list(edge_list, include_single_edges = false) {
  // Call get_lists_of_connected_edges_in_edge_list to get back an array of connected edge lists, then
  // call listify on each one of the edge lists to listify it, put the results in a new array of edge
  // lists + is_open flags and return it.
  var connected_edges_list = get_lists_of_connected_edges_in_edge_list(edge_list, include_single_edges);
  var listified_edges_list = [];
  connected_edges_list.forEach(function(edge_list) {
    if (edge_list.length > 1) {
      var wv_is_open_list = new WrappedValue();
      var listified_edge_list = [];
      var res = uil_listify_edges(edge_list, wv_is_open_list, listified_edge_list);
      if (res)
        listified_edges_list.push({"edges" : listified_edge_list, "is_open" : wv_is_open_list.v});
    }
    else if (edge_list.length == 1) {
      var listified_edge_list = [edge_list[0]];
      listified_edges_list.push({"edges" : listified_edge_list, "is_open" : true});
    }
  });
  return listified_edges_list;
}

export function get_lists_of_connected_edges_in_edge_list(edge_list, include_single_edges = false) {
  var connected_edges_list = [];
  var edges = [... edge_list]; // Local copy so we don't mess with incoming list.

  var found = false;
  var look_for_more_connected_edges = true;
  var new_edges = [];
  var eid = 0;
  var neid = 0;
  var i1;
  var i2;

  while (edges.length > 0) {
    // Start a new list
    var new_list = [];
    // Add seed edge
    new_list.push(edges[0]);
    // Remove seed edge from big list
    edges.splice(0,1);
    // Search for connected edges
    look_for_more_connected_edges = true;
    while (look_for_more_connected_edges) {
      found = false;
      new_edges = [];
      for (i1 = 0; i1 < edges.length; i1++) {
        eid = edges[i1];
        for (i2 = 0; i2 < new_list.length; i2++) {
          neid = new_list[i2];
          if (uil_find_edge_shared_vertex(eid, neid)) {
            found = true;
            if (new_edges.indexOf(eid) == -1)
              new_edges.push(eid);
          }
        }
      }
      if (found) {
        for (i2 = 0; i2 < new_edges.length; i2++) {
          neid = new_edges[i2];
          new_list.push(neid);
          edges.splice(edges.indexOf(neid), 1);
        }
      }
      else {
        look_for_more_connected_edges = false;
      }
      new_edges = [];
    }
    if (new_list.length > 1 || (new_list.length == 1 && include_single_edges))
      connected_edges_list.push(new_list);
    new_list = [];
  }
  return connected_edges_list;
}

export function select_edge_chain(id, must_select, leave_highlighted) {
  // Start from an edge and select adjacent edges that don't share a face with it, keep growing until down.
  var selected_edges = [];
  selected_edges = uid_selected_edges.keys().slice();
  selected_edges.push(id);
  var candidate_edges = [id];
  var edge_id = id;

  // Try to grow
  while (candidate_edges.length > 0) {
    edge_id = candidate_edges[0];
    var vs = uil_get_vertices_of_edge(edge_id);
    // Try to grow one edge in one direction
    var new_edge1 = grow_edge_chain_from_edge(edge_id, vs[0], selected_edges);
    if (new_edge1) {
      selected_edges.push(new_edge1);
      candidate_edges.push(new_edge1);
    }

    var new_edge2 = grow_edge_chain_from_edge(edge_id, vs[1], selected_edges);
    if (new_edge2) {
      selected_edges.push(new_edge2);
      candidate_edges.push(new_edge2);
    }
    candidate_edges.splice(candidate_edges.indexOf[edge_id], 1);
  }

  suppress_animate();
  selected_edges.forEach(function(eid) {
    if (must_select)
      uid_select_object(eid, leave_highlighted);
    else
      uid_deselect_object(eid, leave_highlighted);
  });
  unsuppress_animate_if_ready();
  request_setting_button_states();
}

export function grow_edge_chain_from_edge(eid, vid, selected_edges) {
  var edge_faces = uil_get_visible_faces_of_edge(eid)
  var edges = uil_get_visible_edges_of_vertex(vid);

  // If more than one edges on this vertex were already selected, stop
  var already_selected_edges_on_this_vertex = 0;
  for (var i = edges.length-1; i >= 0; i--) {
    if (selected_edges.indexOf(edges[i]) != -1)
      already_selected_edges_on_this_vertex++;
  }
  if (already_selected_edges_on_this_vertex > 1)
    return null;

  if (edges.length == 2) {
    // It's a 2-vertex, so we proceed to the next edge across from the vertex, unless it's already been selected
    if (edges[0] == eid && selected_edges.indexOf(edges[1]) == -1)
      return (edges[1]);
    else if (edges[1] == eid && selected_edges.indexOf(edges[0]) == -1)
      return (edges[0]);
    else
      return null;
  }

  // See if there's a tangent edge to this one, in which case we select it.
  var found_tangent_edge = 0;
  edges.forEach(function(newid){
    if (newid != eid && selected_edges.indexOf(newid) == -1 &&  uil_is_joint_smooth(eid, newid) == 1)
      found_tangent_edge = newid;
  });
  if (found_tangent_edge > 0)
    return found_tangent_edge;

  /*
  if (edges.length > 4)
    // If more than 4 edges, then there's no way to robustly find a chain
    return null;
  */

    // Remove any edges that were already selected
  for (var i = edges.length-1; i >= 0; i--) {
    if (selected_edges.indexOf(edges[i]) != -1)
      edges.splice(i, 1);
  }

  // Remove any edges that share a face
  for (var i = edges.length-1; i >= 0; i--) {
    var new_edge_faces = uil_get_faces_of_edge(edges[i]);
    if (edge_faces[0] == new_edge_faces[0] || edge_faces[0] == new_edge_faces[1] ||
        edge_faces[1] == new_edge_faces[0] || edge_faces[1] == new_edge_faces[1])
      edges.splice(i, 1);
  }

  // If we're left with just one edge, return it.
  if (edges.length == 1)
    return edges[0];

  // If we're left with more, check whether any edge is at a relatively small angle (say less than 45 degrees), in which case return that one.
  if (edges.length > 1) {
    var best_edge_id = 0;
    var best_edge_angle = Math.PI/4.0; // radians

    var p0 = uil_get_point_at_edge_param(eid, 0);
    var p1 = uil_get_point_at_edge_param(eid, 1);
    var t1;
    if (v3.point_point_distance(p0, uid_vertices.getItem(vid).object3d.position) <
        v3.point_point_distance(p1, uid_vertices.getItem(vid).object3d.position))
      t1 = uil_get_tangent_at_edge_param(eid, 0);
    else
      t1 = uil_get_tangent_at_edge_param(eid, 1);
    edges.forEach(function(neweid){
      if (neweid != eid && selected_edges.indexOf(neweid) == -1) {
        var p0 = uil_get_point_at_edge_param(neweid, 0);
        var p1 = uil_get_point_at_edge_param(neweid, 1);
        var t2;
        if (v3.point_point_distance(p0, uid_vertices.getItem(vid).object3d.position) <
            v3.point_point_distance(p1, uid_vertices.getItem(vid).object3d.position))
          t2 = uil_get_tangent_at_edge_param(neweid, 0);
        else
          t2 = uil_get_tangent_at_edge_param(neweid, 1);

        if (t1.angleTo(t2) < best_edge_angle || t1.angleTo(t2.multiplyScalar(-1)) < best_edge_angle) {
          best_edge_angle = t1.angleTo(t2);
          best_edge_id = neweid;
        }
      }
    });
    if (best_edge_id > 0)
      return best_edge_id;
  }
  return null;
}

export function form_smooth_edge_chain(id) {
  // Start from an edge and select adjacent edges that are smooth with it, keep growing until done.
  var selected_edges = [id];
  var candidate_edges = [id];
  var edge_id = id;

  // Try to grow
  while (candidate_edges.length > 0) {
    edge_id = candidate_edges[0];
    var vs = uil_get_vertices_of_edge(edge_id);
    // Try to grow one edge in one direction
    var new_edge1 = grow_smooth_edge_chain_from_edge(edge_id, vs[0], selected_edges);
    if (new_edge1) {
      selected_edges.push(new_edge1);
      candidate_edges.push(new_edge1);
    }

    var new_edge2 = grow_smooth_edge_chain_from_edge(edge_id, vs[1], selected_edges);
    if (new_edge2) {
      selected_edges.push(new_edge2);
      candidate_edges.push(new_edge2);
    }
    candidate_edges.splice(candidate_edges.indexOf[edge_id], 1);
  }

  return (selected_edges);
}

export function grow_smooth_edge_chain_from_edge(eid, vid, selected_edges) {
  var edges = uil_get_edges_of_vertex(vid);
  // Remove any edges that were already selected or don't share a smooth joint
  for (var i = edges.length-1; i >= 0; i--) {
    if ((selected_edges.indexOf(edges[i]) != -1) || (uil_is_joint_smooth(eid, edges[i]) == 0))
      edges.splice(i, 1);
  }
  // Only return an edge ID if we're left with just one edge.
  if (edges.length == 1)
    return edges[0];
  else
    return null;
}

export function get_neighbouring_edges_of_edge(eid) {

  var vs = uil_get_vertices_of_edge(eid);
  var e1s = uil_get_edges_of_vertex(vs[0]);
  var e2s = uil_get_edges_of_vertex(vs[1]);

  // Debug checks:
  console_log("get_neighbouring_edges_of_edge for: ", eid);
  if (!vs)
    console_log_xml("Error in get_neighbouring_edges_of_edge: vs not defined");
  if (!e1s)
    console_log_xml("Error in get_neighbouring_edges_of_edge: e1s not defined");
  if (!e2s)
    console_log_xml("Error in get_neighbouring_edges_of_edge: e2s not defined");

  var edges = e1s.concat(e2s);
  // Remove original edge
  for (var i = edges.length - 1; i >= 0; i--) {
    if (edges[i] == eid)
      edges.splice(i, 1);
  }
  // Remove duplicates
  edges = [...new Set(edges)];
  return edges;
}

export function get_neighbouring_face_wrt_edge(fid, eid) {
  const fs = uil_get_faces_of_edge(eid);
  return (fs[0] == fid ? fs[1] : fs[0]);
}

export function add_vertex_to_face_border(fid) {
  // Takes a face and adds a new vertex to the middle of the largest edge of it
  // We use it when we want to attach a face to a set of reference faces.
  var edges = uil_get_edges_of_face(fid);
  var longest_edge_id = null;
  var longest_edge_length = 0;
  edges.forEach(function(eid){
    if (!longest_edge_id || uil_get_edge_length(eid) > longest_edge_length) {
      longest_edge_id = eid;
      longest_edge_length = uil_get_edge_length(eid);
    }
  });
  var n = new THREE.Vector3();
  n.x = 0.316227766;
  n.y = 0.948683298;
  n.z = 0.572665981;

  uil_add_vertex_at_param(longest_edge_id, 0.5);
}

export function diagnose_edge(eid) {
  console_log("Diagnosing edge:", eid);

  var e = uid_edges.getItem(eid);
  var n_vertices = e.object3d.geometry.vertices.length;
  var length = uil_get_edge_length(eid);
  var spacing = length/(n_vertices-1);

  if (n_vertices < 200) {
    var msg = "Edge " + eid + " has " + n_vertices + " samples.";
    display_message_to_user(LOG.WARN, msg);
  }

  var n = new THREE.Vector3();
  n.x = 0.316227766;
  n.y = 0.948683298;
  n.z = 0.572665981;
  var v = new THREE.Vector3();
  v.subVectors(e.object3d.geometry.vertices[0], e.object3d.geometry.vertices[n_vertices-1]);
  n.crossVectors(n,v);


  for (var i = 0; i < n_vertices-1; i++) {
    if (v3.point_point_distance(e.object3d.geometry.vertices[i], e.object3d.geometry.vertices[i+1]) > spacing * 1.2) {
      var msg = "Edge " + eid + " has large spacing between vertices: " + i + ", " + (i+1) + ".";
      display_message_to_user(LOG.WARN, msg);
    }
    if (v3.point_point_distance(e.object3d.geometry.vertices[i], e.object3d.geometry.vertices[i+1]) < spacing / 1.8) {
      var msg = "Edge " + eid + " has small spacing between vertices: " + i + ", " + (i+1) + ".";
      display_message_to_user(LOG.WARN, msg);
    }

    if (i < n_vertices-2) {
      var v1 = new THREE.Vector3();
      var v2 = new THREE.Vector3();
      v1.subVectors(e.object3d.geometry.vertices[i], e.object3d.geometry.vertices[i+1]);
      v2.subVectors(e.object3d.geometry.vertices[i+1], e.object3d.geometry.vertices[i+2]);
      /*
      if (eid==97) {
        console_log(i + ": " + Math.abs(v3.cc_angle( v1, v2, n )) + ", " + 10 * Math.PI/180);
        console_log(e.object3d.geometry.vertices[i]);
        console_log(v1);
        console_log(v2);
      }
      */
      if ( !maths.proximate(Math.abs(v3.cc_angle( v1, v2, n )), 2*Math.PI, 10 * Math.PI/180) && !maths.proximate(Math.abs(v3.cc_angle( v1, v2, n )), 0, 10 * Math.PI/180))  {
        var a = Math.min (Math.abs(v3.cc_angle( v1, v2, n )) * 180 / Math.PI , Math.abs(360 - Math.abs(v3.cc_angle( v1, v2, n )) * 180 / Math.PI));
        var msg = "Edge " + eid + " has abrupt turn of " + a + " degrees between vertices: " + i + ", " + (i+2) + ".";
        display_message_to_user(LOG.WARN, msg);
      }
    }

  }
}

export function get_object_from_id(id) {
  if (isFace(id))
    return uid_faces.getItem(id);
  if (isEdge(id))
    return uid_edges.getItem(id);
  if (isVertex(id))
    return uid_vertices.getItem(id);
  return null;
}

export function object_belongs_in_open_body(id) {
  if (isFace(id))
    return uil_is_body_open(uil_get_body_of_face(id));
  else if (isEdge(id))
    return uil_is_body_open(uil_get_body_of_edge(id));
  else if (isVertex(id))
    return uil_is_body_open(uil_get_body_of_vertex(id));
  else {
    console_log_xml("ERROR in object belongs in open body");
    return false;
  }
}

export function new_smoothen_vertex(vid) {
  var v = uid_vertices.getItem(vid);
  var edges = uil_get_edges_of_vertex(vid);
  var center = new THREE.Vector3();
  var total_size = 0;
  var centers_of_mass = [];
  edges.forEach(function(eid){
    var l = uil_get_edge_length(eid);
    total_size += l;
    var c = find_edge_center_of_mass(eid);
    c.multiplyScalar(l);
    centers_of_mass.push(c);
  });
  var first = true;
  centers_of_mass.forEach(function(c){
    c.multiplyScalar(1/total_size);
    if (first)
      center.copy(c);
    else
      center.add(c);
    first = false;
  });

  // XXX ADD VERTICES AT MIDDLES OF ALL EDGES XXX

  // Copy all original samples of edges.
  var edge_samples_hash = new HashTable();
  edges.forEach(function(eid){
    var e = uid_edges.getItem(eid);
    var ar = [...e.object3d.geometry.vertices];
    edge_samples_hash.setItem(eid, ar);
  });

  // Move vertex vid towards center by a factor
  var factor = 0.5;
  var newvec = new THREE.Vector3();
  newvec.copy(v.object3d.position);
  newvec.multiplyScalar(factor);
  center.multiplyScalar(1-factor);
  newvec.add(center);
  uil_move_vertex_to_coords( vid, newvec.x, newvec.y, newvec.z );

  // Calculate new shape for each edge
  edges.forEach(function(eid){
    var e = uid_edges.getItem(eid);
    var vs = uil_get_vertices_of_edge(eid);
    var flip_samples = false;
    if (vs[0] == vid)
      flip_samples = true;
    var samples = edge_samples_hash.getItem(eid);
    var sample_threshold = 0;
    if (flip_samples)
      samples = samples.reverse();

    var start_sample_index = Math.floor(samples.length * sample_threshold);
    var end_sample_index = samples.length-1;
    var dist = v3.point_point_distance(samples[start_sample_index], newvec);

    var dir = new THREE.Vector3();
    dir.copy(newvec);
    //console_log("Target:", dir);
    //console_log("Start:", samples[start_sample_index]);

    //add_small_ball(newvec.x, newvec.y, newvec.z, 256, 0, 0);
    dir.sub(samples[start_sample_index]);
    dir.normalize();
    //console_log("Direction:", dir);
    //console_log("Number of steps:", end_sample_index-start_sample_index);
    var dist = v3.point_point_distance(samples[start_sample_index], newvec);
    //console_log("Distance:", dist);

    var i = 0;
    for (var i = 0; i <= end_sample_index-start_sample_index; i++) {
      var p = new THREE.Vector3();
      var step = new THREE.Vector3();
      step.copy(dir);
      step.multiplyScalar(i/(end_sample_index-start_sample_index)*dist);
      p.copy(samples[start_sample_index]);
      p.add(step);

      // ADD A WEIGHTED AVERAGE BETWEEN AND samples[start_sample_index+i], WITH THE WEIGHT
      // INCREASING LINEARLY WITH I BETWEEN 0 for i = 0 and 1 for i = end_sample_index-start_sample_index
      var w = (i/(end_sample_index-start_sample_index))*(i/(end_sample_index-start_sample_index));
      p.multiplyScalar(w);
      samples[start_sample_index+i].multiplyScalar(1-w);
      samples[start_sample_index+i].add(p);

      //add_small_ball(samples[start_sample_index+i].x, samples[start_sample_index+i].y, samples[start_sample_index+i].z);
    }
    if (flip_samples)
      samples = samples.reverse();
    // Set new edge shape
    uil_modify_edge_geometry(eid, vs[0], vs[1], samples);
    uil_set_edge_freedoms([eid], 5);
    uil_set_edge_freedoms([eid], 4);
  });
  //jcore_toggle_vertex_smoothness(vid);
}

export function can_move_selected_objects() {

  // First examine the special case where only one edge and one vertex of it are selected, where the user
  // wants to rotate the edge tangent. In this case we only care that the edge itself is unfixed.

  if (selected_objects_are_for_rotating_edge_tangent()) {
      return (!uil_is_object_fixed(uid_selected_edges.firstKey()));
  }

  var cannot_proceed_due_to_fixed_objects = false;
  var objects_to_consider = new HashTable();

  uid_selected_faces.keys().forEach(function(fid){
    if (uil_is_object_fixed(fid))
      cannot_proceed_due_to_fixed_objects = true;
    uil_get_edges_of_face(fid).forEach(function(eid){
      objects_to_consider.setItem(eid,eid);
      uil_get_vertices_of_edge(eid).forEach(function(vid){
        objects_to_consider.setItem(vid,vid);
      });
    });
  });
  uid_selected_edges.keys().forEach(function(eid){
    objects_to_consider.setItem(eid,eid);
    uil_get_vertices_of_edge(eid).forEach(function(vid){
      objects_to_consider.setItem(vid,vid);
    });
  });
  uid_selected_vertices.keys().forEach(function(vid){
    objects_to_consider.setItem(vid,vid);
  });

  objects_to_consider.keys().forEach(function(oid){
    if (uil_is_object_fixed(oid))
      cannot_proceed_due_to_fixed_objects = true;
  });

  return (!cannot_proceed_due_to_fixed_objects);
}

export function selected_objects_are_for_rotating_edge_tangent() {
  if (!App.state.editing_edge_tangent_mode)
    return false;

  if (uid_selected_edges.length == 1 && uid_selected_vertices.length == 1 && uid_selected_faces.length == 0 &&
    uil_get_vertices_of_edge(uid_selected_edges.firstKey()).includes(uid_selected_vertices.firstKey()))
      return true;
  else if (uid_selected_edges.length == 1 && uid_selected_vertices.length == 0 && uid_selected_faces.length == 0 &&
    uid_selected_edge_click_point != null)
      return true;
  return false;
}

export function can_delete_selected_objects() {
  var msg = "Unable to delete selected objects";

  if (App.state.editing_reference_sketches) {
    // Only allow deleting face
    if (uid_selected_faces.length == 1) {
      return [true, ""];
    }
    else {
      msg = "You can only delete sketch faces.";
      return [false, msg] ;
    }
  }

  var can_delete = false;
  var body_id = 0;
  var all_body_faces_selected = false;
  // If only edges are selected, make sure they're all from the same body and the body is closed
  if (uid_selected_vertices.length == 0 && uid_selected_edges.length > 0 && uid_selected_faces.length == 0) {
    can_delete = true;
    uid_selected_edges.keys().forEach(function(eid){
      if (body_id == 0)
        body_id = uil_get_body_of_edge(eid);
      else if (uil_get_body_of_edge(eid) != body_id) {
        can_delete = false;
        msg = "Please delete objects from one body at a time";
      }
      if (!can_delete_edge(eid))
        can_delete = false;
    });
  }
  else if (uid_selected_vertices.length > 0 && uid_selected_edges.length == 0 && uid_selected_faces.length == 0) {
    can_delete = true;
    uid_selected_vertices.keys().forEach(function(vid){
      if (body_id == 0)
        body_id = uil_get_body_of_vertex(vid);
      else if (uil_get_body_of_vertex(vid) != body_id) {
        can_delete = false;
        msg = "Please delete objects from one body at a time";
      }
    });
  }
  else if (uid_selected_faces.length > 0) {
    // Only allow deleting faces if all faces of a body are selected, and nothing else
    var body_id = 0;
    var all_same = true;
    uid_selected_faces.keys().forEach(function(id){
      var bid = uil_get_body_of_face(id);
      if (body_id == 0)
        body_id = bid;
      else if (body_id != bid) {
        all_same = false;
        msg = "Please delete objects from one body at a time";
      }
    });
    if (all_same) {
      var all_body_faces_selected = true;
      uid_faces.keys().forEach(function(fid){
        if (uil_is_object_visible(fid) && uil_get_body_of_face(fid) == body_id && !uid_selected_faces.hasItem(fid)) {
          all_body_faces_selected = false;
        }
      });
      if (all_body_faces_selected)
        can_delete = true;
      else {
        can_delete = false;
        msg = "You cannot delete faces, but you can make them invisible using the button in the menu below 'delete'";
      }
    }
  }
  else if (uid_selected_vertices.length > 0 && uid_selected_edges.length > 0) {
    can_delete = false;
    msg = "You cannot simultaneously delete edges and vertices, please select them separately";
  }

  if (can_delete) {
    // Also check that objects are not fixed
    var cannot_proceed_due_to_fixed_objects = false;

    uid_selected_faces.keys().forEach(function(fid){
      if (uil_is_object_fixed(fid))
        cannot_proceed_due_to_fixed_objects = true;
    });
    uid_selected_edges.keys().forEach(function(eid){
      if (uil_is_object_fixed(eid))
        cannot_proceed_due_to_fixed_objects = true;
    });
    uid_selected_vertices.keys().forEach(function(vid){
      if (uil_is_object_fixed(vid))
        cannot_proceed_due_to_fixed_objects = true;
    });

    if (cannot_proceed_due_to_fixed_objects) {
      can_delete = false;
      msg = "At least one fixed object was selected, please deselect it and try again";
    }
  }

  if (uid_raynode != uid_objects_raynode) {
    can_delete = false;
  }

  return [can_delete, msg];
}

export function collect_selected_object_ids_in_array(ar) {
  uid_selected_vertices.values().forEach(function(v) {
    ar.push(v.id);
  });
  uid_selected_edges.values().forEach(function(v) {
    ar.push(v.id);
  });
  uid_selected_faces.values().forEach(function(v) {
    ar.push(v.id);
  });
}

export function collect_selected_object_ids_in_hash(ha) {
  uid_selected_vertices.values().forEach(function(v) {
    ha.setItem(v.id, v);
  });
  uid_selected_edges.values().forEach(function(v) {
    ha.setItem(v.id, v);
  });
  uid_selected_faces.values().forEach(function(v) {
    ha.setItem(v.id, v);
  });
}

export function heal_edge(eid) {
  // Currently only calls start/end drag, to force a smoothening of the edge.
  suppress_animate();
  var res = start_deform_edge(eid);
  var d = new THREE.Vector3( Math.random(), Math.random(), Math.random() ).normalize();
  res = deform_edge(eid, find_edge_midpoint(eid), d);
  res = end_deform_edge(eid);
  unsuppress_animate_if_ready();
}

export function edge_list_is_closed(edge_list) {
  let listified_edge_list = [];
  let wv_is_open_list = new WrappedValue();
  let res = uil_listify_edges(edge_list, wv_is_open_list, listified_edge_list);
  if (res)
    return !wv_is_open_list.v;
  else
    return false;
}

export function find_all_pairs_in_unlistified_edge_list(edge_list) {
  if (edge_list.length < 2)
    return [];

  // Make a copy and remove last edge if same as first, otherwise listify will fail
  let edge_list_copy = [...edge_list];
  if (edge_list_copy[edge_list_copy.length-1] == edge_list_copy[0])
    edge_list_copy.splice(edge_list_copy.length-1, 1);

  let listified_edge_list = [];
  let wv_is_open_list = new WrappedValue();
  let res = uil_listify_edges(edge_list_copy, wv_is_open_list, listified_edge_list);
  if (res)
    return find_all_pairs_in_listified_edge_list(listified_edge_list, wv_is_open_list.v);
  else
    return [];
}

export function find_all_pairs_in_listified_edge_list(listified_edges, is_open) {
  var edge_pairs = [];
  for (var i = 0; i < listified_edges.length-1; i++) {
    var p = [listified_edges[i], listified_edges[i+1]];
    edge_pairs.push(p);
  }
  if (!is_open)
    edge_pairs.push([listified_edges[listified_edges.length-1], listified_edges[0]]);
  return edge_pairs;
}

export function mustNotDisplayFace(fid) {
  if (!uil_is_object_visible(fid))
    return true;

  if (App.state.currently_hiding_faces && isInArray(fid, singled_out_faces_to_hide))
    return true;

  if (App.state.imported_sketches.length > 0 && face_is_for_sketch(fid) && uid_sketch_plane_raynode.children.length == 0)
    return true;

  return false;
}

export function mustNotDisplayEdge(eid) {
  if (!uil_is_object_visible(eid))
    return true;

  if (App.state.currently_hiding_edges && isInArray(eid, singled_out_edges_to_hide))
    return true;

  if (App.state.imported_sketches.length > 0 && edge_is_for_sketch(eid) && uid_sketch_plane_raynode.children.length == 0)
    return true;

  return false;
}

export function mustNotDisplayVertex(vid) {
  if (!uil_is_object_visible(vid))
    return true;

  if (App.state.currently_hiding_vertices && isInArray(vid, singled_out_vertices_to_hide))
    return true;

  if (
    App.state.imported_sketches.length > 0
    && vertex_is_for_sketch(vid)
    && uid_sketch_plane_raynode.children.length == 0
  )
    return true;

  return false;
}

export function isFace(id) {
  return uid_faces.hasItem(id);
}

export function isEdge(id) {
  return uid_edges.hasItem(id);
}

export function isVertex(id) {
  return uid_vertices.hasItem(id);
}

export function check_tangent_edge_angles() {
  // Check that all tangent edge pairs are indeed tangent
  var found_bad_angle = false;
  var worst_angle = 0;
  var bad_angle_text = "These supposedly tangent angles are bad: ";
  uid_edges.keys().forEach(function(e1id){
    uid_edges.keys().forEach(function(e2id){
      if (uil_is_joint_smooth(e1id, e2id)) {
        var vid = uil_find_edge_shared_vertex(e1id, e2id);
        var v1aid = uil_get_vertices_of_edge(e1id)[0];
        var v1bid = uil_get_vertices_of_edge(e1id)[1];
        var t1s = uil_get_edge_end_tangents(e1id);
        var t1;
        if (vid == v1aid)
          t1 = t1s[0];
        else
          t1 = t1s[1];
        var v2aid = uil_get_vertices_of_edge(e2id)[0];
        var v2bid = uil_get_vertices_of_edge(e2id)[1];
        var t2s = uil_get_edge_end_tangents(e2id);
        var t2;
        if (vid == v2aid)
          t2 = t2s[0];
        else
          t2 = t2s[1];

        t1.normalize();
        t2.normalize();
        var a = v3.angle(t1, t2);
        var eps = 1e-06;
        if (!((a < eps && a > (-1) * eps) || (a > Math.PI - eps && a < Math.PI + eps))) {
          if (found_bad_angle)
            bad_angle_text = bad_angle_text + ", ";
          bad_angle_text = bad_angle_text + a + " (" + e1id + ", " + e2id + ") at " + vid;
          found_bad_angle = true;
        }
        // Make a note of this angle
        var wa = Math.abs(a);
        if (wa > Math.PI/2.0)
          wa = Math.abs(Math.PI-wa);
        if (wa > worst_angle)
          worst_angle = wa;
      }
    });
  });
  if (found_bad_angle)
    display_message_to_user(LOG.WARN, bad_angle_text);
  else
    display_message_to_user(LOG.WARN, "No bad angles found (worst was " + worst_angle + ").");
}
